import React from "react";
import { useTranslation } from "react-i18next";
import {
  FooterDesktopContactContainer,
  FooterLegalContainer,
  FooterLinksContainer,
  FooterStyled,
  FooterPrivacyContainer,
  FooterAccreditationContainerDesktop,
  FooterAccredContainerNotDesktop1,
  FooterAccredContainerNotDesktop2,
  FooterUnityContainer,
  FooterUnityImage,
} from "./Footer.styles";

import UnityLogo from "../../assets/logos/unity-rebrand/unity-wordmark.svg";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { FooterLinkInfo } from "./FooterLinkInfo";
import { FooterAccreditationInfoTop } from "./FooterAccreditationInfoTop";
import { FooterAcreditationInfoBottom } from "./FooterAccreditationInfoBottom";
import { LanguageSwitcher } from "../LanguageSwitcher/LanguageSwitcher";

interface FooterProps {
  showLanguageSwitcher?: boolean;
  requireLanguageFeatureFlag?: boolean;
}

export const Footer = ({
  showLanguageSwitcher = false,
  requireLanguageFeatureFlag = true,
}: FooterProps): React.ReactElement => {
  const { isDesktop, deviceType } = useDeviceHook();
  const { t } = useTranslation();

  return (
    <>
      <FooterStyled deviceType={deviceType}>
        <FooterUnityContainer isDesktop={isDesktop}>
          {import.meta.env.VITE_IS_USER_TESTING_ENV !== "true" && (
            <FooterUnityImage src={UnityLogo} alt="Unity logo" />
          )}
        </FooterUnityContainer>
        {isDesktop && (
          <FooterDesktopContactContainer>
            <FooterLinksContainer deviceType={deviceType}>
              <FooterLinkInfo />
            </FooterLinksContainer>
          </FooterDesktopContactContainer>
        )}

        {!isDesktop && (
          <FooterLinksContainer deviceType={deviceType}>
            <FooterLinkInfo />
          </FooterLinksContainer>
        )}

        <FooterLegalContainer isDesktop={isDesktop}>
          {GetTypographyContent({
            content: t("footer.legal.copyright", {
              year: new Date().getFullYear(),
            }),
            size: "small",
          })}
          <FooterPrivacyContainer>
            {GetTypographyContent({
              content: t("footer.links.privacyPolicy"),
              size: "small",
              link: "https://www.billiontoone.com/privacy-policy",
              textColor: "greenDark",
              externalLink: true,
            })}
            &nbsp;|&nbsp;
            {GetTypographyContent({
              content: t("footer.links.noticeOfPrivacyPractices"),
              size: "small",
              textColor: "greenDark",
              link: "https://www.billiontoone.com/privacy-practices",
              externalLink: true,
            })}
            &nbsp;|&nbsp;
            {GetTypographyContent({
              content: t("footer.links.accessibilityStatement"),
              size: "small",
              textColor: "greenDark",
              link: "https://www.billiontoone.com/accessibility-statement",
              externalLink: true,
            })}
            &nbsp;|&nbsp;
            {GetTypographyContent({
              content: t("footer.links.termsOfUse"),
              size: "small",
              textColor: "greenDark",
              link: "https://www.billiontoone.com/terms-of-use",
              externalLink: true,
            })}
          </FooterPrivacyContainer>
          {isDesktop && (
            <>
              <FooterAccreditationContainerDesktop>
                <FooterAccreditationInfoTop />
              </FooterAccreditationContainerDesktop>
              <FooterAccreditationContainerDesktop>
                <FooterAcreditationInfoBottom />
              </FooterAccreditationContainerDesktop>
            </>
          )}
          {!isDesktop && (
            <>
              <FooterAccredContainerNotDesktop1>
                <FooterAccreditationInfoTop />
              </FooterAccredContainerNotDesktop1>
              <FooterAccredContainerNotDesktop2>
                <FooterAcreditationInfoBottom />
              </FooterAccredContainerNotDesktop2>
            </>
          )}
        </FooterLegalContainer>
        <LanguageSwitcher
          showLanguageSwitcher={showLanguageSwitcher}
          requireFeatureFlag={requireLanguageFeatureFlag}
        />
      </FooterStyled>
    </>
  );
};
